/**
 * One comanda, as a card on the board.
 *
 * This was 912 lines: the timers, the mutations, the row rendering, a dish
 * detail modal and a cancel dialog all in one file, which meant not one of its
 * decisions could be checked without a browser and a live service. The
 * decisions now live in `lib/order-card` under test, the clock in
 * `useOrderTimers`, the mutations in `useOrderActions`, and this file is what
 * is left: layout.
 *
 * The header carries the button that opens this comanda across the whole
 * display. The full-screen view renders the same pieces with the same actions,
 * so a cook never closes it to press something.
 */
import { useState } from "react";
import CancelOrderDialog from "./CancelOrderDialog";
import ComandaModal from "./ComandaModal";
import DishDetailModal from "./DishDetailModal";
import OrderCardActions from "./OrderCardActions";
import OrderCardHeader from "./OrderCardHeader";
import OrderCardItems from "./OrderCardItems";
import { useOrderActions } from "../hooks/useOrderActions";
import { useOrderTimers } from "../hooks/useOrderTimers";
import { CARD_BORDER_CLASS, orderCardFlags, visibleItemsFor } from "../lib/order-card";
import { useTranslation } from "../i18n";
import type { KdsT } from "../lib/i18n/settings";

/** Where a comanda came from, as the card names it. Unknown sources print as sent. */
const ORDER_SOURCE_LABEL: Record<string, (t: KdsT) => string> = {
  PHONE: (t) => t("kds.orderSource.phone", "Telefono"),
  WAITER: (t) => t("kds.orderSource.waiter", "Cameriere"),
  QR: (t) => t("kds.orderSource.qr", "QR Code"),
  CRM: (t) => t("kds.orderSource.crm", "CRM"),
};

interface OrderCardProps {
  order: any;
  compact?: boolean;
  stationId?: string;
  /** Set by the full-screen view, which supplies its own frame and controls. */
  embedded?: boolean;
}

export default function OrderCard({ order, compact, stationId, embedded }: OrderCardProps) {
  const { t } = useTranslation();
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [fullScreen, setFullScreen] = useState(false);

  const items = visibleItemsFor(order, stationId);
  const timers = useOrderTimers(order, items);
  const actions = useOrderActions(order);
  const flags = orderCardFlags({ order, items });

  const currentWave = order?.currentWave ?? order?.currentCourse ?? 1;
  const cardBorder = CARD_BORDER_CLASS[order?.status] || "border-kds-muted";

  const body = (
    <>
      <OrderCardItems
        items={items}
        compact={compact}
        now={timers.now}
        waveFiredAt={timers.waveFiredAt}
        activeWave={timers.activeWave}
        isOverdue={timers.isOverdue}
        isWarning={timers.isWarning}
        loadingItemIds={actions.loadingItemIds}
        onAdvance={actions.advanceItem}
        onOpenDish={setDetailItem}
      />

      {order?.notes && (
        <div className="mt-2 text-kds-note text-kds-muted bg-kds-card/30 rounded-lg px-3 py-1.5">
          &#x1F4DD; {order.notes}
        </div>
      )}
    </>
  );

  const footer = (
    <OrderCardActions
      compact={compact}
      hasCookingFired={flags.hasCookingFired}
      showFireCourse={!compact && flags.hasUnfiredWave}
      showComplete={!compact && flags.allItemsDone}
      nextWave={currentWave + 1}
      loadingAction={actions.loadingAction}
      onMarkAllReady={() => actions.markAllReady(items)}
      onFireNextWave={actions.fireNextWave}
      onComplete={actions.completeOrder}
    />
  );

  // Rendered inside the full-screen view: no card chrome, no nested modal.
  if (embedded) {
    return (
      <>
        {body}
        {footer}
        {detailItem && (
          <DishDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
        )}
      </>
    );
  }

  return (
    <div
      className={`bg-kds-surface rounded-xl border-l-4 ${cardBorder} p-3 ${
        order?.priority > 0 ? "ring-2 ring-kds-priority" : ""
      } ${timers.isTimerRunning ? "animate-pulse-card" : ""}`}
    >
      <OrderCardHeader
        order={order}
        elapsed={timers.elapsed}
        showTimer={timers.activeWaveFiredAt !== null}
        isOverdue={timers.isOverdue}
        isWarning={timers.isWarning}
        onOpenFull={() => setFullScreen(true)}
        onRequestCancel={() => setCancelConfirmOpen(true)}
        compact={compact}
      />

      {/* Where the comanda came from, and who for. Not in a station column:
          a cook cooks the same plate whether a waiter typed it or it arrived
          from the QR menu, and in that venue the customer's name is where
          somebody writes the table — so this row printed the table a second
          time, under a title that had been truncated to "T...". */}
      {!compact && (
        <div className="flex items-center gap-3 text-kds-meta text-kds-muted mb-2">
          <span className="bg-kds-card px-2 py-0.5 rounded">
            {ORDER_SOURCE_LABEL[order?.source]?.(t) || order?.source}
          </span>
          {order?.customerName && <span>{order.customerName}</span>}
        </div>
      )}

      {body}
      {footer}

      {fullScreen && (
        <ComandaModal
          target={{ kind: "station-order", orderId: order.id, stationId }}
          // `order` is re-handed down by react-query on every refetch, so the
          // open view follows the board instead of freezing at open time.
          source={{ orders: [order] }}
          compact={compact}
          onClose={() => setFullScreen(false)}
        />
      )}

      {detailItem && (
        <DishDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}

      {cancelConfirmOpen && (
        <CancelOrderDialog
          order={order}
          itemCount={items.length}
          onConfirm={actions.cancelOrder}
          onDismiss={() => setCancelConfirmOpen(false)}
        />
      )}
    </div>
  );
}
